/**
 * React Query wrappers for the editable mail copy (#1911).
 *
 * One list query and two mutations. There is no per-key query: five rows
 * arrive in one response, and a second endpoint keyed by template would let
 * the editor and the notice composer's "load a template" picker disagree about
 * what the current copy says.
 */
import type {
  EmailTemplate,
  EmailTemplateKey,
  EmailTemplateTestSendRequest,
  EmailTemplateTestSendResponse,
  UpdateEmailTemplateRequest,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  listEmailTemplates,
  testSendEmailTemplate,
  updateEmailTemplate,
} from './emailTemplates.ts';

export const EMAIL_TEMPLATES_KEY = ['email-templates'] as const;

export function useEmailTemplates(): UseQueryResult<readonly EmailTemplate[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: EMAIL_TEMPLATES_KEY,
    queryFn: ({ signal }) => listEmailTemplates(fetchImpl, signal),
  });
}

export function useUpdateEmailTemplate(): UseMutationResult<
  EmailTemplate,
  Error,
  { readonly key: EmailTemplateKey; readonly body: UpdateEmailTemplateRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, body }) => updateEmailTemplate(key, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EMAIL_TEMPLATES_KEY });
    },
  });
}

export function useTestSendEmailTemplate(): UseMutationResult<
  EmailTemplateTestSendResponse,
  Error,
  { readonly key: EmailTemplateKey; readonly body: EmailTemplateTestSendRequest }
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: ({ key, body }) => testSendEmailTemplate(key, body, fetchImpl),
  });
}
